// Utilitaires de regroupement des règlements (décaissements partageant un même
// groupeReglementCodeId) pour produire un ordre de dépense / fiche / quittance
// UNIQUE couvrant plusieurs natures (ex. Provision + Frais médicaux).

const NATURE_LABELS = {
    "SINISTRE EN PRINCIPAL": "Sinistre en principal",
    PROVISION: "Provision",
    ACCESSOIRES: "Frais accessoires",
    HONORAIRES: "Honoraires",
};

export const natureLabel = (nature) => NATURE_LABELS[nature] || nature || "—";

// Motif de règlement imposé lorsqu'on paie un ÉTABLISSEMENT (hôpital, garage… →
// decaissement.beneficiaireMoral) : il s'agit toujours du règlement de leurs factures.
export const MOTIF_ETABLISSEMENT = "LE RÈGLEMENT DE FACTURES";
export const estBeneficiaireEtablissement = (d) => !!d?.beneficiaireMoral;

/**
 * Motif à afficher sur les documents de règlement (ordre de dépense, quittance, fiches…).
 * Règle : on affiche TOUJOURS le motif réellement enregistré (motifReglement, à défaut
 * formuleDeFin). Le libellé générique établissement (« LE RÈGLEMENT DE FACTURES ») n'est
 * utilisé QU'EN L'ABSENCE de motif enregistré ET si le bénéficiaire est une personne morale.
 * Sinon, le `fallback` propre au document.
 */
export const motifReglementAffiche = (d, fallback = "") => {
    const motif = (d?.motifReglement || d?.formuleDeFin || "").toString().trim();
    if (motif) return motif;
    if (estBeneficiaireEtablissement(d)) return MOTIF_ETABLISSEMENT;
    return fallback;
};

const num = (v) => parseFloat(v) || 0;

const sinistreNumero = (sinistre) => sinistre?.numeroSinistreM || sinistre?.numeroSinistreP || sinistre?.numSinistre || "—";
const policeNumero = (sinistre) => sinistre?.contrat?.numeroPoliceM || sinistre?.contrat?.numeroPoliceP || "—";
const immatriculation = (sinistre) => sinistre?.engin?.immatriculation || sinistre?.engin?.numeroImmatriculation || "—";
const assureNom = (sinistre) => {
    const souscripteur = sinistre?.contrat?.souscripteur;
    if (!souscripteur) return "—";
    return souscripteur?.prenom
        ? `${souscripteur.nom || ""} ${souscripteur.prenom || ""}`.trim()
        : souscripteur?.nom || "—";
};

const dossierKey = (d) => d?.sinistre?.codeId || sinistreNumero(d?.sinistre);

/**
 * Agrège une liste de décaissements d'un même groupe en un « décaissement
 * synthétique » : montants cumulés + détail ligne par ligne. Les champs
 * d'en-tête (bénéficiaire, représentant, motif, formule…) proviennent de la
 * première ligne (toutes partagent le même bénéficiaire dans un groupe).
 *
 * Le résultat est directement utilisable par les documents existants
 * (montantGlobal = total) et porte en plus `lignes` + `hasProvision`.
 */
export const aggregateGroupe = (list = []) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    // Les lignes REJETÉES ne figurent NI sur l'ordre de dépense / quittance NI dans les totaux.
    const kept = list.filter((d) => d?.etatDecaissement !== "REJETE");
    if (kept.length === 0) return null;
    const first = kept[0];

    const montantGlobal = kept.reduce((s, d) => s + num(d?.montantGlobal), 0);
    const montantPaye = kept.reduce((s, d) => s + num(d?.montantPaye), 0);
    const taxes = kept.reduce((s, d) => s + num(d?.taxes), 0);

    // Chèque du LOT : un seul chèque couvre tout le groupe, mais la ligne de tête (first) n'est pas
    // forcément celle qui porte les champs chèque. On les prend depuis N'IMPORTE QUELLE ligne qui les
    // renseigne → la référence du chèque s'affiche bien sur la quittance groupée.
    const ligneCheque = kept.find((d) => d?.numeroCheque) || first;

    return {
        ...first,
        numeroCheque: ligneCheque?.numeroCheque || first?.numeroCheque || null,
        banque: ligneCheque?.banque || first?.banque || null,
        dateEmissionCheque: ligneCheque?.dateEmissionCheque || first?.dateEmissionCheque || null,
        // Mode de règlement du lot (chèque/virement) : repris de la ligne qui porte la référence,
        // pour que la quittance groupée affiche « Virement réf. … » le cas échéant.
        modePaiement: ligneCheque?.modePaiement || first?.modePaiement || null,
        montantGlobal,
        montantPaye: montantPaye || null,
        taxes,
        lignes: kept.map((d) => ({
            nature: d?.nature,
            // Nature absente (règlements multiples experts/conseils…) : repli sur le
            // libellé du décaissement plutôt que "—".
            label: natureLabel(d?.nature || d?.libelleDecaissement),
            montant: num(d?.montantGlobal),
            numero: d?.numero,
            sinistre: d?.sinistre,
            // Infos dossier par ligne (tableau de l'ordre de dépense groupé).
            numeroSinistre: sinistreNumero(d?.sinistre),
            dateSinistre: d?.sinistre?.dateSinistre,
            numeroPolice: policeNumero(d?.sinistre),
            immatriculation: immatriculation(d?.sinistre),
            assure: assureNom(d?.sinistre),
        })),
        dossiers: Array.from(kept.reduce((map, d) => {
            const sinistre = d?.sinistre;
            const key = dossierKey(d);
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    numeroSinistre: sinistreNumero(sinistre),
                    dateSinistre: sinistre?.dateSinistre,
                    numeroPolice: policeNumero(sinistre),
                    immatriculation: immatriculation(sinistre),
                    assure: assureNom(sinistre),
                    montant: 0,
                });
            }
            map.get(key).montant += num(d?.montantGlobal);
            return map;
        }, new Map()).values()),
        hasProvision: kept.some((d) => `${d?.nature || ""}`.toUpperCase().includes("PROVISION")),
        isReglementMultiple: kept.length > 1,
    };
};

/**
 * Colonnes dossier d'une ligne de groupe, avec repli sur un sinistre de
 * référence (impressions depuis le détail sinistre, où les lignes ne portent
 * pas leur propre objet sinistre — les helpers renvoient alors "—").
 */
export const dossierDeLigne = (ligne, sinistreRef) => ({
    numeroSinistre: ligne?.numeroSinistre && ligne.numeroSinistre !== "—"
        ? ligne.numeroSinistre
        : (sinistreRef?.numeroSinistreM || sinistreRef?.numSinistre || ""),
    dateSinistre: ligne?.dateSinistre || sinistreRef?.dateSinistre,
    numeroPolice: ligne?.numeroPolice && ligne.numeroPolice !== "—"
        ? ligne.numeroPolice
        : (sinistreRef?.contrat?.numeroPoliceM || sinistreRef?.contrat?.numeroPoliceP || ""),
    immatriculation: ligne?.immatriculation && ligne.immatriculation !== "—"
        ? ligne.immatriculation
        : (sinistreRef?.engin?.immatriculation || ""),
    assure: ligne?.assure && ligne.assure !== "—"
        ? ligne.assure
        : `${sinistreRef?.contrat?.souscripteur?.nom || ""} ${sinistreRef?.contrat?.souscripteur?.prenom || ""}`.trim(),
});

/**
 * Regroupe une liste plate de décaissements :
 * - clé = groupeReglementCodeId quand présent, sinon le codeId de la ligne ;
 * - renvoie [{ key, isGroup, groupeCodeId, lignes, total, ... }].
 */
export const groupDecaissements = (decaissements = []) => {
    const map = new Map();
    (decaissements || []).forEach((d) => {
        const key = d?.groupeReglementCodeId || `__solo__${d?.codeId}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(d);
    });
    return Array.from(map.entries()).map(([key, lignes]) => ({ key, lignes }));
};
